// Persistance chiffrée de l'état MLS, et enrôlement de l'appareil.
//
// L'état MLS d'un device (`toState()`) contient des **clés privées** : son
// identité de signature, ses KeyPackages, les secrets du groupe. Le confier en
// clair au serveur reviendrait à lui remettre l'identité de l'appareil — et à
// annuler tout l'intérêt d'un serveur aveugle.
//
// Il est donc chiffré ici, avec une sous-clé dédiée de la clé maîtresse :
//
//     K ──HKDF("mls-state")──► clé d'état ──XChaCha20-Poly1305──► blob
//
// L'AAD lie chaque blob à sa destination (ce device, ce dossier). Un serveur
// malveillant qui servirait à un appareil l'état d'un autre serait démasqué :
// l'authentification couvre l'AAD, donc le déchiffrement échouerait.

import { api, ApiError } from '../api/client';
import {
  MlsClient,
  MlsGroup,
  decryptBlob,
  deriveSubkey,
  encryptBlob,
  loadCrypto,
} from './index';

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (b64: string) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const enc = new TextEncoder();

/** Clé de chiffrement des états, dérivée de K pour ce seul usage. */
const stateKey = (masterKey: Uint8Array) => deriveSubkey(masterKey, 'mls-state');

/** AAD de l'état client : le blob n'est valide que pour CE device. */
const clientAad = (deviceId: string) => enc.encode(`delo/device-state/v1:${deviceId}`);

/** AAD de l'état de groupe : valide pour ce device ET ce dossier seulement. */
const groupAad = (deviceId: string, dossierId: string) =>
  enc.encode(`delo/group-state/v1:${deviceId}:${dossierId}`);

/** Réponse du serveur à la lecture d'un état. */
type StoredState = { encryptedState: string; revision: number; epoch: string | null };

function isNotFound(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

// ---------------------------------------------------------------------------
// Enrôlement de l'appareil
// ---------------------------------------------------------------------------

/**
 * Identifiant local de l'appareil. Rien de secret (le serveur le connaît), donc
 * `localStorage` convient — c'est même nécessaire : il doit survivre à la
 * fermeture de l'onglet, sinon l'appareil serait ré-enrôlé à chaque visite.
 *
 * Indexé sur (**slug du cabinet**, e-mail), et non sur des identifiants qu'on
 * n'a pas encore : la session doit porter son `deviceId` **dès le login** (il y
 * est figé), or ni l'`userId` ni le `tenantId` ne sont connus avant. Le slug,
 * lui, est dans l'adresse.
 */
const deviceKey = (tenantSlug: string, email: string) =>
  `delo.deviceId.${tenantSlug}.${email.toLowerCase()}`;

export function rememberedDeviceId(
  tenantSlug: string,
  email: string,
): string | null {
  return localStorage.getItem(deviceKey(tenantSlug, email));
}

export function forgetDevice(tenantSlug: string, email: string): void {
  localStorage.removeItem(deviceKey(tenantSlug, email));
  localStorage.removeItem(pendingKey(tenantSlug, email));
}

/**
 * Identité MLS d'un appareil **en attente d'approbation**, gardée localement et
 * **chiffrée** (elle contient sa clé privée de signature).
 *
 * Pourquoi la garder : un appareil en attente ne peut rien écrire côté serveur —
 * c'est précisément ce qui le rend inoffensif. Mais s'il perdait son identité au
 * moindre rechargement de page, l'empreinte que le propriétaire vient de
 * comparer et d'approuver ne correspondrait plus à rien, et il faudrait tout
 * recommencer.
 */
const pendingKey = (tenantSlug: string, email: string) =>
  `delo.pendingClient.${tenantSlug}.${email.toLowerCase()}`;

const PENDING_AAD = enc.encode('delo/pending-client/v1');

export type DeviceSummary = {
  id: string;
  signaturePublicKey: string;
  isPrimary: boolean;
  status: string;
  createdAt: string;
};

/**
 * Enrôle cet appareil auprès du serveur, à partir de l'identité MLS du client.
 *
 * Le serveur n'apprend que la clé **publique** de signature — juste assez pour
 * corréler l'appareil à sa feuille MLS. La session appelante est rattachée au
 * device créé (côté serveur), ce qui la rend apte à écrire son état.
 */
async function enrollDevice(
  sessionToken: string,
  client: MlsClient,
): Promise<DeviceSummary> {
  return api<DeviceSummary>('/devices', {
    method: 'POST',
    token: sessionToken,
    body: { signaturePublicKey: toBase64(client.signaturePublicKey()) },
  });
}

// ---------------------------------------------------------------------------
// État client (identité + keystore du device)
// ---------------------------------------------------------------------------

export type DeviceSession = {
  deviceId: string;
  client: MlsClient;
  /** Vrai si l'identité vient d'être créée (premier usage de cet appareil). */
  created: boolean;
  /** Seul l'appareil principal peut approuver un nouvel appareil. */
  isPrimary: boolean;
  /** Révision serveur du dernier état écrit — pour la concurrence optimiste. */
  revision: number;
};

/** Appareil en attente : il a demandé, il n'a encore le droit de rien. */
export type PendingDevice = {
  deviceId: string;
  /** À comparer, à l'œil, avec ce qu'affiche l'appareil principal. */
  signaturePublicKey: Uint8Array;
};

export type DeviceOutcome =
  | { kind: 'ready'; device: DeviceSession }
  | { kind: 'pending'; device: PendingDevice };

/** Les appareils de l'appelant (le principal s'en sert pour approuver). */
export function listDevices(sessionToken: string): Promise<DeviceSummary[]> {
  return api<DeviceSummary[]>('/devices', { token: sessionToken });
}

/**
 * Rend cet appareil opérationnel : identité MLS restaurée depuis le serveur, ou
 * créée puis enrôlée si c'est son premier usage.
 *
 * C'est cette fonction qui rend une session **reprenable** : sans elle, chaque
 * connexion repartirait d'une identité neuve, et l'appareil perdrait sa place
 * dans tous les groupes MLS auxquels il appartient.
 *
 * Renvoie `null` si le serveur refuse l'enrôlement : la policy « device
 * primaire » veut qu'un appareil secondaire soit approuvé depuis l'appareil
 * principal. L'utilisateur est alors connecté, mais sans identité MLS — donc
 * sans accès aux dossiers. On ne fait pas échouer la connexion pour autant :
 * il doit pouvoir se connecter pour comprendre ce qu'on attend de lui.
 */
export async function openDeviceSession(
  sessionToken: string,
  masterKey: Uint8Array,
  tenantSlug: string,
  email: string,
  userId: string,
): Promise<DeviceOutcome> {
  await loadCrypto();
  const key = stateKey(masterKey);
  const known = rememberedDeviceId(tenantSlug, email);

  // Où en est cet appareil, du point de vue du serveur ?
  const devices = await listDevices(sessionToken);
  const mine = known ? devices.find((d) => d.id === known) : undefined;

  // --- Appareil connu et approuvé : on reprend son identité ---------------
  if (mine?.status === 'active') {
    // Le `deviceId` d'une session est figé à son émission : si l'appareil vient
    // d'être approuvé, la session courante ne le porte pas encore.
    await api(`/devices/${mine.id}/bind`, { method: 'POST', token: sessionToken });

    const pending = takePendingClient(tenantSlug, email, masterKey);
    if (pending) {
      // Il vient d'être approuvé : on dépose enfin son état, et on oublie la
      // copie locale.
      const revision = await saveClientState(
        sessionToken,
        masterKey,
        mine.id,
        pending,
        0,
      );
      return {
        kind: 'ready',
        device: {
          deviceId: mine.id,
          client: pending,
          created: true,
          isPrimary: mine.isPrimary,
          revision,
        },
      };
    }

    const stored = await api<StoredState>('/device-state', { token: sessionToken });
    const state = decryptBlob(
      key,
      fromBase64(stored.encryptedState),
      clientAad(mine.id),
    );
    return {
      kind: 'ready',
      device: {
        deviceId: mine.id,
        client: MlsClient.fromState(state),
        created: false,
        isPrimary: mine.isPrimary,
        revision: stored.revision,
      },
    };
  }

  // --- Appareil connu, toujours en attente ---------------------------------
  if (mine?.status === 'pending') {
    const client = takePendingClient(tenantSlug, email, masterKey, false);
    if (client) {
      return {
        kind: 'pending',
        device: {
          deviceId: mine.id,
          signaturePublicKey: client.signaturePublicKey(),
        },
      };
    }
    // Identité locale perdue : la demande en cours ne correspond plus à rien.
    forgetDevice(tenantSlug, email);
  }

  // --- Appareil inconnu : on crée une identité et on demande ----------------
  const client = new MlsClient(userId);
  const device = await enrollDevice(sessionToken, client);
  localStorage.setItem(deviceKey(tenantSlug, email), device.id);

  if (device.status === 'pending') {
    // Il ne peut RIEN écrire côté serveur tant qu'il n'est pas approuvé — c'est
    // ce qui le rend inoffensif. Son identité est donc gardée ici, chiffrée, le
    // temps que le propriétaire compare l'empreinte et approuve.
    savePendingClient(tenantSlug, email, masterKey, client);
    return {
      kind: 'pending',
      device: {
        deviceId: device.id,
        signaturePublicKey: client.signaturePublicKey(),
      },
    };
  }

  await api(`/devices/${device.id}/bind`, { method: 'POST', token: sessionToken });
  const revision = await saveClientState(sessionToken, masterKey, device.id, client, 0);
  return {
    kind: 'ready',
    device: {
      deviceId: device.id,
      client,
      created: true,
      isPrimary: device.isPrimary,
      revision,
    },
  };
}

/**
 * **Reprend** un appareil perdu : cette session en redevient l'identité.
 *
 * La sortie de l'impasse d'après-récupération. L'avocat qui a oublié son mot de
 * passe ET perdu son appareil a retrouvé sa clé grâce aux parts Shamir — mais il
 * arrive sur un navigateur neuf, dont l'appareil attend une approbation que
 * **personne ne peut donner** : son appareil principal n'existe plus.
 *
 * Faire approuver un appareil neuf ne le sauverait pas : ce serait une feuille
 * MLS neuve, qu'aucun membre de ses groupes ne pourrait ajouter, et qui ne verrait
 * rien de l'historique. Il faut **redevenir l'ancien appareil**, dont l'état est
 * déposé sur le serveur, chiffré sous K. Le serveur rend un blob ; seule K
 * l'ouvre. Même feuille, même place dans les groupes, tout le passé.
 *
 * Si le déchiffrement échoue, c'est que le blob n'est pas celui de cet appareil
 * (ou pas chiffré sous cette clé) : on ne prétend pas avoir repris quoi que ce
 * soit.
 */
export async function reclaimDevice(
  sessionToken: string,
  masterKey: Uint8Array,
  tenantSlug: string,
  email: string,
  deviceId: string,
  abandonDeviceId?: string,
): Promise<DeviceSession> {
  await loadCrypto();

  const device = await api<DeviceSummary>(`/devices/${deviceId}/reclaim`, {
    method: 'POST',
    token: sessionToken,
    body: { abandonDeviceId },
  });

  const stored = await api<StoredState>('/device-state', { token: sessionToken });
  const state = decryptBlob(
    stateKey(masterKey),
    fromBase64(stored.encryptedState),
    clientAad(device.id),
  );

  // L'identité en attente n'a plus d'objet, et cet appareil est désormais le
  // nôtre : c'est lui que ce navigateur doit retrouver aux prochaines visites.
  localStorage.removeItem(pendingKey(tenantSlug, email));
  localStorage.setItem(deviceKey(tenantSlug, email), device.id);

  return {
    deviceId: device.id,
    client: MlsClient.fromState(state),
    created: false,
    isPrimary: device.isPrimary,
    revision: stored.revision,
  };
}

/** Range l'identité d'un appareil en attente, chiffrée. */
function savePendingClient(
  tenantSlug: string,
  email: string,
  masterKey: Uint8Array,
  client: MlsClient,
): void {
  const blob = encryptBlob(stateKey(masterKey), client.toState(), PENDING_AAD);
  localStorage.setItem(pendingKey(tenantSlug, email), toBase64(blob));
}

/** Relit l'identité en attente. `consume` l'efface (l'appareil est approuvé). */
function takePendingClient(
  tenantSlug: string,
  email: string,
  masterKey: Uint8Array,
  consume = true,
): MlsClient | null {
  const raw = localStorage.getItem(pendingKey(tenantSlug, email));
  if (!raw) return null;
  try {
    const state = decryptBlob(stateKey(masterKey), fromBase64(raw), PENDING_AAD);
    if (consume) localStorage.removeItem(pendingKey(tenantSlug, email));
    return MlsClient.fromState(state);
  } catch {
    localStorage.removeItem(pendingKey(tenantSlug, email));
    return null;
  }
}

/** Dépose l'état du client, chiffré. Renvoie la nouvelle révision serveur. */
export async function saveClientState(
  sessionToken: string,
  masterKey: Uint8Array,
  deviceId: string,
  client: MlsClient,
  baseRevision?: number,
): Promise<number> {
  await loadCrypto();
  const blob = encryptBlob(stateKey(masterKey), client.toState(), clientAad(deviceId));
  const { revision } = await api<{ revision: number }>('/device-state', {
    method: 'PUT',
    token: sessionToken,
    body: { encryptedState: toBase64(blob), baseRevision },
  });
  return revision;
}

// ---------------------------------------------------------------------------
// État d'un dossier (un par device × dossier)
// ---------------------------------------------------------------------------

/** Un message déjà déchiffré, conservé pour l'affichage. */
export type CachedMessage = {
  id: string;
  authorId: string;
  /** `null` = message illisible par cet appareil (voir plus bas). */
  text: string | null;
  at: string;
};

/**
 * Tout ce qu'un appareil doit savoir d'un dossier — **dans une seule enveloppe
 * chiffrée**.
 *
 * Cette réunion n'est pas un détail de rangement, c'est une nécessité : MLS est
 * un ratchet, **un message ne se déchiffre qu'une fois**. Si l'état MLS et la
 * liste des messages déjà traités pouvaient diverger (l'un sauvegardé sans
 * l'autre), on rejouerait des messages sur un ratchet déjà avancé, ou on
 * sauterait des messages qu'on ne saurait plus jamais lire. Les deux sont donc
 * écrits ensemble, dans le même blob, en une seule requête.
 *
 * `messages` garde les clairs déchiffrés : la confidentialité persistante de MLS
 * interdit de les redéchiffrer plus tard. Sans ce cache, l'historique
 * disparaîtrait à chaque rechargement de page.
 */
export type DossierState = {
  /** État OpenMLS du groupe (`group.toState()`). */
  mls: Uint8Array;
  /** Identifiants des messages déjà appliqués au ratchet. */
  processed: string[];
  /** Historique déchiffré, dans l'ordre. */
  messages: CachedMessage[];
  /**
   * Clé du dossier — **partagée par tous ses membres**.
   *
   * La clé maîtresse est personnelle : chiffrer un document avec elle le
   * rendrait illisible pour les autres participants. Il faut donc un secret
   * commun au dossier. Il est tiré au hasard à la création, puis distribué **par
   * le groupe MLS lui-même** (message applicatif), qui est précisément le canal
   * dont on dispose pour parler à tous les membres sans que le serveur écoute.
   *
   * `null` tant qu'on ne l'a pas reçue (on vient de rejoindre, le partage n'est
   * pas encore arrivé).
   */
  dossierKey: Uint8Array | null;
};

/** Sérialisation de l'enveloppe (JSON, les octets en base64). */
function encodeState(state: DossierState): Uint8Array {
  return new TextEncoder().encode(
    JSON.stringify({
      v: 2,
      mls: toBase64(state.mls),
      processed: state.processed,
      messages: state.messages,
      dossierKey: state.dossierKey ? toBase64(state.dossierKey) : null,
    }),
  );
}

function decodeState(bytes: Uint8Array): DossierState {
  const raw = JSON.parse(new TextDecoder().decode(bytes)) as {
    mls: string;
    processed: string[];
    messages: CachedMessage[];
    dossierKey?: string | null;
  };
  return {
    mls: fromBase64(raw.mls),
    processed: raw.processed ?? [],
    messages: raw.messages ?? [],
    dossierKey: raw.dossierKey ? fromBase64(raw.dossierKey) : null,
  };
}

/** Restaure l'état du dossier pour cet appareil, ou `null` si rien n'est persisté. */
export async function loadDossierState(
  sessionToken: string,
  masterKey: Uint8Array,
  deviceId: string,
  dossierId: string,
): Promise<{ state: DossierState; revision: number } | null> {
  await loadCrypto();
  try {
    const stored = await api<StoredState>(`/dossiers/${dossierId}/device-state`, {
      token: sessionToken,
    });
    const plain = decryptBlob(
      stateKey(masterKey),
      fromBase64(stored.encryptedState),
      groupAad(deviceId, dossierId),
    );
    return { state: decodeState(plain), revision: stored.revision };
  } catch (e) {
    if (isNotFound(e)) return null;
    throw e;
  }
}

/**
 * Dépose l'état du dossier, chiffré.
 *
 * L'`epoch` accompagne le blob **en clair** : le serveur ne peut pas lire
 * l'état, mais il refuse une écriture qui ferait *reculer* l'epoch — un
 * garde-fou contre l'écrasement par un état périmé.
 */
export async function saveDossierState(
  sessionToken: string,
  masterKey: Uint8Array,
  deviceId: string,
  dossierId: string,
  state: DossierState,
  epoch: bigint,
  baseRevision?: number,
): Promise<number> {
  await loadCrypto();
  const blob = encryptBlob(
    stateKey(masterKey),
    encodeState(state),
    groupAad(deviceId, dossierId),
  );
  const { revision } = await api<{ revision: number }>(
    `/dossiers/${dossierId}/device-state`,
    {
      method: 'PUT',
      token: sessionToken,
      body: {
        encryptedState: toBase64(blob),
        epoch: epoch.toString(),
        baseRevision,
      },
    },
  );
  return revision;
}

/** Reconstruit le groupe MLS depuis l'enveloppe. */
export function groupOf(state: DossierState): MlsGroup {
  return MlsGroup.fromState(state.mls);
}
